import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartSolid } from '@fortawesome/pro-solid-svg-icons';
import {
  faArrowAltCircleDown,
  faArrowRotateRight,
  faHeart,
  faTrash,
  faBan,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import HoverDropdown from '../../../../vendor/libs/hover-dropdown';
import Authorize from '../../../Authorize';
import { removeOrigin } from '../../../../helpers/urls';
import { RequestError } from '../../../../helpers/fetch';
import { regenerateSelfieSnapshot } from '../../../../helpers/api';
import { displayErrorModal } from '../../../../helpers/swal';
import env from '../../../../config/env';
import { banHandler, deleteHandler } from '../../actionHelper';
import { toggleFavorite } from '../../actions';
import { PHOTOS_WRITE } from '../../../Authorize/permissions/resources';
import { DELETE_PHOTO, BAN_PHOTO_USER } from '../../permissions';
import { setLargeImageUrl, setRegeneratePending, unsetFullscreenSnapshot } from '../actions';

import style from './style.module.scss';

const REGENERATE_TIMEOUT_MS = 90_000;

const Controls = ({
  snapshot,
  regeneratePendingSnapshotId,
  toggleFavoriteAction,
  unsetFullscreenSnapshotAction,
  setLargeImageUrlAction,
  setRegeneratePendingAction,
}) => {
  const snapshotId = snapshot?._id;
  const isRegenerating = Boolean(
    snapshotId
    && regeneratePendingSnapshotId != null
    && String(regeneratePendingSnapshotId) === String(snapshotId),
  );

  const shimmerTimeoutRef = useRef(null);
  useEffect(() => {
    if (isRegenerating) {
      shimmerTimeoutRef.current = setTimeout(() => {
        setRegeneratePendingAction(null);
      }, REGENERATE_TIMEOUT_MS);
    }
    return () => {
      if (shimmerTimeoutRef.current) {
        clearTimeout(shimmerTimeoutRef.current);
        shimmerTimeoutRef.current = null;
      }
    };
  }, [isRegenerating]);

  if (!snapshot) {
    return null;
  }

  const {
    favorite,
    rawURI,
    rawEnhancedURI,
    sharedURI,
    hidden,
    userId,
    camera,
    inboundSource,
    selfiecam,
    magicshot,
  } = snapshot;

  const isAiApp = env.USER_APP_TYPE === 'ai';
  const isSelfieCamSnapshot = camera === 'selfie-cam' || inboundSource === 'selfie-cam';
  const showRegenerate = isAiApp && isSelfieCamSnapshot && selfiecam?.inputs && !magicshot;

  const banBtnHandler = (userBan) => (evt) => {
    evt.stopPropagation();
    if (userBan) {
      banHandler(!hidden, snapshotId, userId);
    } else {
      banHandler(!hidden, snapshotId);
    }
  };

  const deleteBtnHandler = async () => {
    const isDeleted = await deleteHandler(snapshotId, sharedURI);
    if (isDeleted) {
      unsetFullscreenSnapshotAction();
      setLargeImageUrlAction(null);
    }
  };

  const regenerateHandler = async (evt) => {
    evt.stopPropagation();
    if (isRegenerating) {
      return;
    }
    setRegeneratePendingAction(String(snapshotId));
    try {
      await regenerateSelfieSnapshot(snapshotId);
    } catch (err) {
      setRegeneratePendingAction(null);
      const message = err instanceof RequestError && err.body?.error
        ? err.body.error
        : err?.message || 'Could not start regeneration.';
      displayErrorModal({ text: message });
    }
  };

  return (
    <>
      {showRegenerate && (
        <Authorize or permissions={[PHOTOS_WRITE, DELETE_PHOTO]}>
          <div className={style.topRightControl}>
            <OverlayTrigger
              placement="left"
              overlay={(
                <Tooltip className={style.tooltip}>
                  {isRegenerating ? 'Regenerating…' : 'Regenerate AI image'}
                </Tooltip>
              )}
            >
              <span className={style.regenerateTriggerWrap}>
                <button
                  type="button"
                  className={classnames('btn-primary', { [style.regenerateBtnBusy]: isRegenerating })}
                  disabled={isRegenerating}
                  onClick={regenerateHandler}
                  aria-label={isRegenerating ? 'Regenerating AI image' : 'Regenerate AI image'}
                  aria-busy={isRegenerating || undefined}
                >
                  <FontAwesomeIcon
                    icon={faArrowRotateRight}
                    className={classnames({ [style.regenerateIconSpin]: isRegenerating })}
                    aria-hidden
                  />
                </button>
              </span>
            </OverlayTrigger>
          </div>
        </Authorize>
      )}
      <div className={style.controls}>
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip className={style.tooltip}>Favorite</Tooltip>}
        >
          <button type="button" onClick={() => toggleFavoriteAction(snapshot)} className="btn-primary">
            <FontAwesomeIcon icon={favorite ? faHeartSolid : faHeart} />
          </button>
        </OverlayTrigger>
        <Authorize permissions={BAN_PHOTO_USER}>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{hidden ? 'Unban' : 'Ban'}</Tooltip>}
          >
            <HoverDropdown
              key={hidden}
              onClick={(evt) => evt.stopPropagation()}
              as={DropdownButton}
              variant="default"
              className={classnames('dropdown-toggle-hide-arrow btn-primary', { active: hidden })}
              title={<FontAwesomeIcon icon={faBan} />}
            >
              <Dropdown.Item onClick={banBtnHandler(false)}>{`${hidden ? 'Unban' : 'Ban'} Photo`}</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={banBtnHandler(true)}>{`${hidden ? 'Unban' : 'Ban'} User`}</Dropdown.Item>
            </HoverDropdown>
          </OverlayTrigger>
        </Authorize>

        <OverlayTrigger
          placement="top"
          overlay={<Tooltip className={style.tooltip}>Download</Tooltip>}
        >
          <HoverDropdown
            onClick={(evt) => evt.stopPropagation()}
            as={DropdownButton}
            variant="default"
            className="dropdown-toggle-hide-arrow btn-primary"
            title={<FontAwesomeIcon icon={faArrowAltCircleDown} />}
          >
            <Dropdown.Item href={removeOrigin(rawEnhancedURI || rawURI)} download>
              Raw
            </Dropdown.Item>
            <Dropdown.Divider />
            {sharedURI && (
              <Dropdown.Item href={removeOrigin(sharedURI)} download>
                Overlayed
              </Dropdown.Item>
            )}
          </HoverDropdown>
        </OverlayTrigger>

        <Authorize or permissions={[PHOTOS_WRITE, DELETE_PHOTO]}>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip className={style.tooltip}>Delete</Tooltip>}
          >
            <button type="button" onClick={deleteBtnHandler} className="btn-primary">
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </OverlayTrigger>

        </Authorize>
      </div>
    </>
  );
};


export default connect(
  ({
    lightbox: {
      snapshot: {
        data: snapshot,
        regeneratePendingSnapshotId,
      },
    },
  }) => ({ snapshot, regeneratePendingSnapshotId }),
  {
    toggleFavoriteAction: toggleFavorite,
    unsetFullscreenSnapshotAction: unsetFullscreenSnapshot,
    setLargeImageUrlAction: setLargeImageUrl,
    setRegeneratePendingAction: setRegeneratePending,
  },
)(Controls);
